import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  Clock,
  FileText,
  Link2,
  Linkedin,
  MessageCircle,
  Tag,
} from "lucide-react";
import { ClosingCTA } from "@/components/common/ClosingCTA";
import { Seo } from "@/components/common/Seo";
import { IMAGES } from "@/constants";
import { blogService } from "@/services/blogService"; // apna path daal lena
import { BlogContent } from "./BlogContent"; // apna path daal lena

const RELATED_LIMIT = 3;

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/* ---------------------------------------------------------- skeletons */

function DetailSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-pulse">
      <div className="h-4 w-28 bg-slate-200 rounded" />
      <div className="mt-8 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 lg:gap-12">
        <div className="space-y-4">
          <div className="aspect-[16/9] bg-slate-200 rounded-2xl" />
          <div className="h-4 w-2/3 bg-slate-200 rounded" />
          <div className="h-9 bg-slate-200 rounded w-11/12" />
          <div className="h-4 bg-slate-200 rounded" />
          <div className="h-4 bg-slate-200 rounded w-5/6" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: RELATED_LIMIT }).map((_, i) => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

function RelatedSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: RELATED_LIMIT }).map((_, i) => (
        <div key={i} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="h-20 w-24 rounded-lg bg-slate-200 shrink-0" />
          <div className="flex-1 space-y-2 py-1">
            <div className="h-3 w-1/3 bg-slate-200 rounded" />
            <div className="h-3 bg-slate-200 rounded" />
            <div className="h-3 w-2/3 bg-slate-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------- related */

function RelatedItem({ p }) {
  return (
    <Link
      to={`/blog/${p.slug}`}
      className="group flex gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-[#F5A623] hover:shadow-md"
    >
      <div className="h-20 w-24 sm:h-24 sm:w-28 shrink-0 overflow-hidden rounded-lg bg-slate-100">
        <img
          src={p.image || IMAGES.laptopStudy}
          alt={p.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
        <div>
          {p.category?.name && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#F5A623]">
              {p.category.name}
            </span>
          )}
          <h3 className="mt-0.5 text-sm font-semibold leading-snug text-slate-900 line-clamp-2 transition-colors group-hover:text-amber-700">
            {p.title}
          </h3>
        </div>
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
          <span className="truncate">{p.authorName}</span>
          <span className="h-1 w-1 shrink-0 rounded-full bg-slate-300" />
          <span className="inline-flex shrink-0 items-center gap-1">
            <Clock className="h-3 w-3" /> {p.readTime} min
          </span>
        </div>
      </div>
    </Link>
  );
}

function RelatedBlogs({ items, loading }) {
  return (
    <aside className="lg:sticky lg:top-24 self-start" data-testid="related-blogs">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold text-slate-900">Related blogs</h2>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="mt-1 h-0.5 w-10 rounded bg-[#F5A623]" />

        <div className="mt-4">
          {loading ? (
            <RelatedSkeleton />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
              <FileText className="h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">No related blogs</p>
              <p className="mt-1 text-xs text-slate-500">Is category me abhi koi aur blog nahi hai.</p>
            </div>
          ) : (
            // mobile: 1 column, tablet: 2/3 columns, desktop sidebar: 1 column
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-1">
              {items.map((p) => (
                <RelatedItem key={p._id} p={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

/* ---------------------------------------------------------- page */

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    window.scrollTo({ top: 0 });
    setLoading(true);
    setRelatedLoading(true);
    setNotFound(false);
    setError("");
    setRelated([]);

    blogService
      .getBySlug(slug)
      .then((res) => !cancelled && setBlog(res.data))
      .catch((err) => {
        if (cancelled) return;
        setBlog(null);
        if (err?.response?.status === 404) setNotFound(true);
        else setError(err?.response?.data?.message || "Blog load nahi ho paaya. Dobara try karo.");
      })
      .finally(() => !cancelled && setLoading(false));

    blogService
      .getRelated(slug, { limit: RELATED_LIMIT })
      .then((res) => !cancelled && setRelated((res.data || []).slice(0, RELATED_LIMIT)))
      .catch(() => !cancelled && setRelated([]))
      .finally(() => !cancelled && setRelatedLoading(false));

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard block ho to ignore */
    }
  };

  if (loading) return <DetailSkeleton />;

  if (notFound || error || !blog) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {notFound ? "Blog nahi mila" : "Kuch galat ho gaya"}
        </h1>
        <p className="mt-2 text-slate-500">
          {notFound ? "Ye blog hata diya gaya hai ya link galat hai." : error}
        </p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F5A623] px-5 py-2 text-sm font-semibold text-slate-950"
        >
          <ArrowLeft className="h-4 w-4" /> Saare blogs dekho
        </Link>
      </div>
    );
  }

  const seo = blog.seo || {};
  const title = seo.metaTitle || blog.title;
  const description = seo.metaDescription || blog.shortDescription;
  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(blog.title);
  const initial = (blog.authorName || "?").trim().charAt(0).toUpperCase();
  const shareBtn =
    "inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 transition hover:border-[#F5A623] hover:text-slate-900";

  return (
    <div data-testid="blog-detail-page">
      <Seo title={title} path={`/blog/${blog.slug}`} description={description} />

      {seo.jsonSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(seo.jsonSchema) }} />
      )}

      <section className="bg-white py-8 sm:py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-sm font-medium text-slate-600 transition hover:border-[#F5A623] hover:text-amber-700"
          >
            <ArrowLeft className="h-4 w-4" /> Back to blogs
          </Link>

          <div className="mt-6 sm:mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-12">
            {/* LEFT: main blog */}
            <article className="min-w-0">
              {/* Image */}
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
                <img
                  src={blog.image || IMAGES.laptopStudy}
                  alt={blog.title}
                  className="aspect-[16/9] w-full object-cover"
                />
                {blog.category?.name && (
                  <span className="absolute left-3 top-3 sm:left-4 sm:top-4 rounded-full bg-[#F5A623] px-3 py-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-slate-950 shadow">
                    {blog.category.name}
                  </span>
                )}
              </div>

              {/* Author, date, read time */}
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-3 text-sm text-slate-500">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-[#F5A623]">
                    {initial}
                  </span>
                  <span className="font-semibold text-slate-800">{blog.authorName}</span>
                </div>
                <span className="hidden h-4 w-px bg-slate-300 sm:block" />
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" /> {formatDate(blog.date)}
                </span>
                <span className="hidden h-4 w-px bg-slate-300 sm:block" />
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {blog.readTime} min read
                </span>
              </div>

              {/* Title */}
              <h1 className="mt-5 font-heading text-2xl font-bold leading-tight text-slate-900 sm:text-3xl lg:text-4xl">
                {blog.title}
              </h1>

              {/* Short description */}
              <p className="mt-4 border-l-4 border-[#F5A623] pl-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                {blog.shortDescription}
              </p>

              {/* Full description */}
              <BlogContent html={blog.fullDescription} />

              {/* Tags */}
              {blog.tags?.length > 0 && (
                <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-6">
                  <Tag className="h-4 w-4 text-slate-400" />
                  {blog.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              )}

              {/* Share */}
              <div className="mt-6 flex flex-wrap items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <span className="mr-1 text-sm font-semibold text-slate-700">Share:</span>
                <button onClick={copyLink} className={shareBtn}>
                  {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Link2 className="h-3.5 w-3.5" />}
                  {copied ? "Copied!" : "Copy link"}
                </button>
                
                 <a href={`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={shareBtn}
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
                
                 <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={shareBtn}
                >
                  <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                </a>
              </div>
            </article>

            {/* RIGHT: sticky related blogs (mobile pe neeche) */}
            <RelatedBlogs items={related} loading={relatedLoading} />
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}